// @flow
import React from 'react'
import { Link, graphql } from 'gatsby'
import { format, isPast } from 'date-fns'
import Layout from '../layout'
import Container from '../components/container'
import { LogoText } from '../components/logo'
import Speakers, { invitedSpeakers } from '../components/speakers'
import Programme from '../components/programme'
import Button from '../components/button'
import ShowDate from '../components/date'
import Icon from '../components/icon'
// import DateCounter from '../components/date-counter'
import styles from './index.module.css'

type Props = {
  data: {
    site: {
      siteMetadata: {
        dates: {
          conferenceStart: string,
          conferenceEnd: string,
          submitAbstractBy: string,
          regularRegistrationBy: string,
        },
      },
    },
  },
}

function HomePage({ data }: Props) {
  const { dates } = data.site.siteMetadata
  return (
    <Layout isHeaderFloating hasContainer={false}>
      <div className={styles.jumbotron}>
        <Container className={styles.container}>
          <h1 className={styles.title}>
            <LogoText className={styles.text} />
          </h1>
          <p className={styles.tagline}>
            The 6th conference on scholarly publishing in the context of open
            science.
          </p>
          <div className={styles.duration}>
            <Icon className={styles.icon} id="calendar" size={24} />
            <div>
              <time dateTime={format(dates.conferenceStart, 'YYYY-MM-DD')}>
                {format(dates.conferenceStart, 'D')}
              </time>
              –
              <time dateTime={format(dates.conferenceEnd, 'YYYY-MM-DD')}>
                {format(dates.conferenceEnd, 'D MMMM YYYY')}
              </time>
            </div>
          </div>
          <div className={styles.venue}>
            <Link to="/venue">
              <Icon className={styles.icon} id="location" size={24} />
              <span>Zadar, Croatia</span>
            </Link>
          </div>
          <div className={styles.buttons}>
            <div>
              <Button
                className={styles.button}
                variant="inverted"
                as={Link}
                to="/call-for-submissions"
              >
                <Icon id="file" size={24} />
                <span>Call for Submissions</span>
              </Button>
              <p>
                Submit your abstract by:
                <br />
                <ShowDate date={dates.submitAbstractBy} />
                {isPast(
                  dates.submitAbstractBy[dates.submitAbstractBy.length - 1],
                ) ? (
                  <>
                    <br />
                    The deadline has passed.
                    <br />
                    <Link to="/contact-us">Contact us</Link> for inquiries about
                    the submissions.
                  </>
                ) : null}
              </p>
            </div>
            <div>
              <Button
                className={styles.button}
                variant="inverted"
                as={Link}
                to="/registration"
                // isDisabled={isPast(dates.regularRegistrationBy)}
              >
                <Icon id="enter" size={24} />
                <span>Registration</span>
              </Button>
              {/* <p>
                Register by:
                <br />
                <ShowDate date={dates.regularRegistrationBy} />
                {isPast(dates.regularRegistrationBy) ? (
                  <>
                    <br />
                    Afterwards you can only register on site.
                  </>
                ) : null}
              </p> */}
            </div>
          </div>
        </Container>
      </div>
      <div className={styles.about}>
        <Container>
          <h2>About</h2>
          <p>
            We welcome you to take part in a conference that strives to present
            and discuss the plurality of approaches to the scholarly publishing
            and assessment (metrics) and new trends in the scholarly publishing
            area. The PUBMET2019 conference will present innovative approaches,
            best practice discussions and take on future challenges. The
            organizers aim to maintain lively discussion among experts in
            various fields involved in scholarly publishing and dissemination of
            knowledge. The field of scholarly publishing and metrics includes
            all disciplines and variety of skills: researchers from different
            research areas, information specialists and librarians, editors,
            publishers, educators and students, policy makers and managers
            contribute to this field of investigation.
          </p>
          {/* <DateCounter /> */}
        </Container>
      </div>
      <div className={styles.opportunities}>
        <Container className={styles.container}>
          <h2>Opportunities</h2>
          <div>
            <h3 className={styles.title}>
              <Icon className={styles.icon} id="earth" />
              Discover
            </h3>
            <p>
              The lectures will cover broad topics on Open Science, including
              Open Access, open data, open repositories, and open peer review,
              publication and editorial practices, copyright and licensing
              issues, plagiarism and other ethical considerations, advances in
              digital scholarly publishing, and scholarly metrics and
              assessment.
            </p>
          </div>
          <div>
            <h3 className={styles.title}>
              <Icon className={styles.icon} id="light-bulb" />
              Learn
            </h3>
            <p>
              On the pre-conference day 18 September 2019 several workshops will
              offer opportunity for participants to acquire additional knowledge
              on Creative Commons licences, Plan S initiative, new publishing
              models and academic writing.
            </p>
          </div>
          <div>
            <h3 className={styles.title}>
              <Icon className={styles.icon} id="speech-bubbles" />
              Discuss
            </h3>
            <p>
              A great opportunity to meet your colleagues, researchers, editors
              and publishers to discuss new trends, products, and to join some
              great initiatives.
            </p>
          </div>
          <div>
            <h3 className={styles.title}>
              <Icon className={styles.icon} id="graduation" />
              Ask the Expert
            </h3>
            <p>
              There will be plenty opportunities for formal and informal
              discussions and to raise questions on the topics vital for your
              work and profession.
            </p>
          </div>
        </Container>
      </div>
      <div className={styles.speakers}>
        <Container className={styles.container}>
          <h2>Invited speakers</h2>
          <Speakers
            className={styles.list}
            includeSpeakers={invitedSpeakers}
            isCentered
          />
          <Button className={styles.button} as={Link} to="/speakers">
            See all speakers
          </Button>
        </Container>
      </div>
      <div className={styles.programme}>
        <Container>
          <h2>Programme</h2>
          <Programme isCompact />
        </Container>
      </div>
      <div className={styles.register}>
        <Container className={styles.container}>
          <Button
            size="large"
            as={Link}
            to="/registration"
            target="_blank"
            rel="noopener noreferrer"
          >
            Register for the conference
          </Button>
        </Container>
      </div>
    </Layout>
  )
}

export const query = graphql`
  query HomePage {
    site {
      siteMetadata {
        dates {
          conferenceStart
          conferenceEnd
          submitAbstractBy
          regularRegistrationBy
        }
      }
    }
  }
`

export default HomePage
